/**
 * BenchmarkRunsTable - Displays a table of benchmark runs
 *
 * Uses ProjectTable components from shared-ui for consistent retro styling.
 * Rows are clickable to navigate to run detail view.
 * Includes delete button for cleaning up stuck/old runs.
 */

import React, { useState, useCallback } from 'react';
import {
    Box,
    Typography,
    TableRow,
    TableCell,
    IconButton,
    Tooltip,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import {
    vortxColors,
    vortxTypography,
    ProjectTableContainer,
    ProjectTableHead,
    ProjectTableHeaderCell,
    ProjectTableBody,
} from '@vortx/shared-ui';

const PHD_API_BASE = process.env.REACT_APP_PHD_API_URL || 'http://localhost:5002';

const STATUS_COLORS = {
    submitted: '#9b59b6',
    queued: '#9b59b6',
    running: '#2196F3',
    completed: '#5fd4c4',
    failed: vortxColors.error,
};

function formatRelativeTime(isoString) {
    if (!isoString) return '--';
    const diff = Date.now() - new Date(isoString).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
}

function getProgressCounts(progress) {
    if (!progress) return { completed: 0, total: 0 };
    let completed = 0;
    let total = 0;
    for (const [key, val] of Object.entries(progress)) {
        if (key.startsWith('task_') && val && typeof val === 'object') {
            completed += val.completed || 0;
            total += val.total || 0;
        }
    }
    if (total === 0 && progress.completed != null) {
        completed = progress.completed;
        total = progress.total || 0;
    }
    return { completed, total };
}

function formatDuration(startIso, endIso) {
    if (!startIso) return '--';
    const start = new Date(startIso).getTime();
    const end = endIso ? new Date(endIso).getTime() : Date.now();
    const secs = Math.floor((end - start) / 1000);
    if (secs < 60) return `${secs}s`;
    const mins = Math.floor(secs / 60);
    const remSecs = secs % 60;
    if (mins < 60) return `${mins}m ${remSecs}s`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}h ${mins % 60}m`;
}

export default function BenchmarkRunsTable({ runs, onSelectRun }) {
    const [deleting, setDeleting] = useState({});

    const handleDelete = useCallback(async (e, runId) => {
        e.stopPropagation(); // Don't trigger row click
        if (!window.confirm('Delete this benchmark run? This cannot be undone.')) return;

        setDeleting(prev => ({ ...prev, [runId]: true }));
        try {
            const response = await fetch(
                `${PHD_API_BASE}/api/coordination/benchmark-runs/${runId}`,
                { method: 'DELETE' }
            );
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.error || `HTTP ${response.status}`);
            }
            // Firestore listener will automatically remove it from the list
        } catch (err) {
            console.error('[BenchmarkRunsTable] Delete failed:', err);
            alert(`Delete failed: ${err.message}`);
        } finally {
            setDeleting(prev => ({ ...prev, [runId]: false }));
        }
    }, []);

    if (!runs || runs.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.8rem',
                    color: '#888',
                }}>
                    No benchmark runs yet
                </Typography>
            </Box>
        );
    }

    return (
        <ProjectTableContainer>
            <ProjectTableHead>
                <ProjectTableHeaderCell sx={{ width: 40 }}>Status</ProjectTableHeaderCell>
                <ProjectTableHeaderCell>Label</ProjectTableHeaderCell>
                <ProjectTableHeaderCell>Suite</ProjectTableHeaderCell>
                <ProjectTableHeaderCell align="center">Progress</ProjectTableHeaderCell>
                <ProjectTableHeaderCell align="right">Avg PoP</ProjectTableHeaderCell>
                <ProjectTableHeaderCell align="right">Started</ProjectTableHeaderCell>
                <ProjectTableHeaderCell align="right">Duration</ProjectTableHeaderCell>
                <ProjectTableHeaderCell sx={{ width: 40 }} />
            </ProjectTableHead>
            <ProjectTableBody>
                {runs.map((run) => {
                    const statusColor = STATUS_COLORS[run.status] || '#888';
                    const progress = run.progress || {};
                    const avgPop = run.summary?.mean_cost_pop_pct;
                    const isDeleting = deleting[run.run_id];

                    return (
                        <TableRow
                            key={run.run_id}
                            onClick={() => onSelectRun?.(run.run_id)}
                            sx={{
                                cursor: 'pointer',
                                opacity: isDeleting ? 0.4 : 1,
                                transition: 'opacity 0.3s',
                                '&:hover': {
                                    backgroundColor: 'rgba(155, 89, 182, 0.15)',
                                },
                            }}
                        >
                            <TableCell sx={{ borderBottom: '1px solid rgba(155, 89, 182, 0.2)' }}>
                                <Box sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    backgroundColor: statusColor,
                                    boxShadow: `0 0 6px ${statusColor}80`,
                                }} />
                            </TableCell>
                            <TableCell sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.8rem',
                                color: vortxColors.white,
                                borderBottom: '1px solid rgba(155, 89, 182, 0.2)',
                            }}>
                                {run.label || run.run_id.slice(0, 8)}
                            </TableCell>
                            <TableCell sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.75rem',
                                color: '#aaa',
                                textTransform: 'uppercase',
                                borderBottom: '1px solid rgba(155, 89, 182, 0.2)',
                            }}>
                                {run.suite}
                            </TableCell>
                            <TableCell align="center" sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.75rem',
                                color: '#aaa',
                                borderBottom: '1px solid rgba(155, 89, 182, 0.2)',
                            }}>
                                {(() => {
                                    const p = getProgressCounts(progress);
                                    return `${p.completed}/${run.total_scenarios || p.total}`;
                                })()}
                            </TableCell>
                            <TableCell align="right" sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: avgPop != null ? '#5fd4c4' : '#666',
                                borderBottom: '1px solid rgba(155, 89, 182, 0.2)',
                            }}>
                                {avgPop != null ? `${avgPop.toFixed(1)}%` : '--'}
                            </TableCell>
                            <TableCell align="right" sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.75rem',
                                color: '#888',
                                borderBottom: '1px solid rgba(155, 89, 182, 0.2)',
                            }}>
                                {formatRelativeTime(run.submitted_at)}
                            </TableCell>
                            <TableCell align="right" sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.75rem',
                                color: '#888',
                                borderBottom: '1px solid rgba(155, 89, 182, 0.2)',
                            }}>
                                {formatDuration(run.submitted_at, run.completed_at)}
                            </TableCell>
                            <TableCell align="center" sx={{
                                borderBottom: '1px solid rgba(155, 89, 182, 0.2)',
                                padding: '4px',
                            }}>
                                <Tooltip title="Delete run" placement="left">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => handleDelete(e, run.run_id)}
                                        disabled={isDeleting}
                                        sx={{
                                            color: '#555',
                                            opacity: 0,
                                            transition: 'opacity 0.2s, color 0.2s',
                                            'tr:hover &': { opacity: 1 },
                                            '&:hover': { color: vortxColors.error },
                                        }}
                                    >
                                        <DeleteIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    );
                })}
            </ProjectTableBody>
        </ProjectTableContainer>
    );
}
